import { getPartnerEncumbrances } from 'modules/partners/api/getPartnerEncumbrances';
import { useCallback, useEffect, useState } from 'react';
import { Encumbrance } from 'types';

type UseGetPartnerEncumbrancesProps = {
  partnerId?: string;
  page?: number;
  rowsPerPage?: number;
};

export const useGetPartnerEncumbrances = ({
  partnerId,
  page = 1,
  rowsPerPage = 10,
}: UseGetPartnerEncumbrancesProps) => {
  const [encumbrances, setEncumbrances] = useState<Encumbrance[]>([]);
  const [totalEncumbrances, setTotalEncumbrances] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchPartnerEncumbrances = useCallback(
    async (controller: AbortController) => {
      if (!partnerId) return;

      try {
        setIsLoading(true);

        const response = await getPartnerEncumbrances({
          partnerId,
          params: { page, items: rowsPerPage },
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setEncumbrances(response.data);
          setTotalEncumbrances(response.total);
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsError(true);
        }
      }
    },
    [partnerId, page, rowsPerPage]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchPartnerEncumbrances(controller);
    return () => controller.abort();
  }, [fetchPartnerEncumbrances]);

  return {
    data: {
      encumbrances,
      totalEncumbrances,
    },
    isLoading,
    isError,
  };
};
